import { Modal } from '@/components/modal/Modal';
import { useModal } from '@/components/modal/useModal';
import SiCalendar from '@/icons/SiCalendar';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import * as React from 'react';
import { Day, useLilius } from 'use-lilius';
import { InputContainer } from '../InputContainer';
import { InputProps, withField } from '../withField';
import { DatePicker, DayLabels } from './DatePicker';

const defaultLabels = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tues',
  Wednesday: 'Wed',
  Thursday: 'Thurs',
  Friday: 'Fri',
  Saturday: 'Sat'
};

interface DateInputProps {
  labels?: DayLabels;
  className?: string;
  excludeDates?: string[];
  excludePastDates?: boolean;
  disabledPredicate?: (date: Date) => boolean;
  placeholder?: string;
}

export const DateInput: React.FC<InputProps<Date> & DateInputProps> = ({
  value,
  onChange,
  className,
  labels = defaultLabels,
  excludeDates,
  excludePastDates,
  disabledPredicate,
  placeholder
}) => {
  const { calendar, inRange, isSelected, select, selected, setViewing, viewing, viewNextMonth, viewPreviousMonth, clearSelected } =
    useLilius({ weekStartsOn: Day.MONDAY });

  const lastSyncedValue = React.useRef<number | undefined | null>(undefined);

  React.useEffect(() => {
    const currentTime = value?.getTime() || null;
    
    // Bail out if the value hasn't actually changed since the last time we synced it.
    // This prevents unstable useLilius functions from resetting the viewing state when the user navigates months.
    if (lastSyncedValue.current !== undefined && lastSyncedValue.current === currentTime) {
      return;
    }
    lastSyncedValue.current = currentTime;

    if (!value) {
      setViewing(new Date());
      clearSelected();
      return;
    }
    
    setViewing(value);
    select(value, true);
  }, [value, select, setViewing, clearSelected]);

  const modalProps = useModal();

  return (
    <InputContainer
      role="button"
      onClick={() => modalProps.open()}
      className={cn(className, 'cursor-pointer justify-between gap-3 px-3 py-2 text-sm')}
    >
      {selected[0] ? (
        <span>{format(selected[0], 'dd/MM/yyyy')}</span>
      ) : (
        <span className="text-secondary-std italic opacity-60">{placeholder || 'No date selected'}</span>
      )}
      <SiCalendar className="text-2xl" />

      <Modal className="max-w-md bg-white" {...modalProps}>
        <div className="sm:w-sm w-full pb-4">
          <DatePicker
            dismiss={() => modalProps.close()}
            calendar={calendar[0]}
            selected={selected}
            viewing={viewing}
            inRange={inRange}
            isSelected={isSelected}
            viewPreviousMonth={viewPreviousMonth}
            viewNextMonth={viewNextMonth}
            onDayClick={day => {
              onChange?.(day);
              modalProps.close();
            }}
            labels={labels}
            excludeDates={excludeDates}
            excludePastDates={excludePastDates}
            disabledPredicate={disabledPredicate}
          />
        </div>
      </Modal>
    </InputContainer>
  );
};

export const DateField = withField<Date, DateInputProps>(DateInput);
